/**
 * Stitch-optics renderer.
 *
 * This is not a preview in the disposable sense — the result of a confirmed
 * mapping becomes the design object's image and is what production receives, so
 * it renders twice: small and fast while the seller adjusts dropdowns, then once
 * at print resolution on confirm. Every stitch parameter is expressed as a
 * fraction of the output edge so both renders are the same picture at two sizes.
 * Fixed pixel values would turn visible hatching at 512 px into a near-flat
 * field at 3500 px, which means the seller would approve something else than
 * what ships.
 */

import type { PaletteEntry, ThreadColor } from '@designdeck/shared';
import { containScale, hexToRgb } from '@designdeck/shared';
import { DPI_THRESHOLDS } from '@/config/printArea';
import { mapPixelsToThreads, type ThreadMask } from './mapPixelsToThreads';

/** Edge the stitch constants below were tuned against. */
const REFERENCE_EDGE = 512;

const HATCH_SPACING = 2.6 / REFERENCE_EDGE;
const HATCH_LINE_WIDTH = 1.1 / REFERENCE_EDGE;
const SHADOW_BLUR = 1.5 / REFERENCE_EDGE;
const SHADOW_OFFSET_Y = 1 / REFERENCE_EDGE;

/** Rotating hatch directions, so adjacent thread areas read as separate fills. */
const HATCH_ANGLES = [45, -45, 0, 90, 25, -65, 70, -20];

const HATCH_DARK = 'rgba(0,0,0,0.20)';
const HATCH_LIGHT = 'rgba(255,255,255,0.22)';
const SHADOW_COLOR = 'rgba(0,0,0,0.35)';

/** Interactive edge used while the modal is open. */
export const PREVIEW_EDGE = 512;

/** Assets API rejects anything larger, so the confirm render must stay under it. */
const MAX_ASSET_EDGE = 10_000;

/** Used when the decoration method carries no minDpi. */
const FALLBACK_DPI = DPI_THRESHOLDS.GOOD;

/**
 * Edge for the render that ships. Derived from the motif's *placed* size, not
 * the print area: a small logo inside a 30 cm area would otherwise be rendered
 * by the ratio between the two, wasting pixels and memory on nothing.
 */
export function printResolutionEdge(params: {
  /** Placed width of the design object on the product, in millimetres. */
  widthMm: number;
  /** Placed height of the design object on the product, in millimetres. */
  heightMm: number;
  /** DecorationMethod.minDpi; the column is nullable and unset for embroidery. */
  minDpi?: number | null;
  /** Natural edge of the source image — rendering beyond it invents detail. */
  sourceEdge: number;
}): number {
  const dpi = params.minDpi ?? FALLBACK_DPI;
  const longestMm = Math.max(params.widthMm, params.heightMm);
  const required = Math.ceil((longestMm / 25.4) * dpi);
  return Math.max(
    1,
    Math.min(required, Math.max(1, params.sourceEdge), MAX_ASSET_EDGE)
  );
}

export interface RenderOptions {
  image: HTMLImageElement;
  palette: PaletteEntry[];
  mapping: Record<string, string>;
  threads: ThreadColor[];
  /** Longest edge of the output canvas. */
  maxEdge: number;
}

function createCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

/** Output dimensions: the image aspect fitted into a square of `maxEdge`. */
function outputSize(
  image: { naturalWidth?: number; naturalHeight?: number },
  maxEdge: number
): { width: number; height: number } {
  const sourceWidth = image.naturalWidth || 1;
  const sourceHeight = image.naturalHeight || 1;
  // Never upscale — rendering past the source resolution invents detail.
  const scale = Math.min(
    containScale(sourceWidth, sourceHeight, maxEdge, maxEdge),
    1
  );
  return {
    width: Math.max(1, Math.round(sourceWidth * scale)),
    height: Math.max(1, Math.round(sourceHeight * scale)),
  };
}

/**
 * Paint one thread's area: flat thread color through the mask, then hatching,
 * then a metallic sheen. Composited with `source-atop` so nothing leaks outside
 * the mask.
 */
function paintThreadLayer(
  layer: ThreadMask,
  width: number,
  height: number,
  angleDeg: number,
  scale: number
): HTMLCanvasElement {
  const canvas = createCanvas(width, height);
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  const { r, g, b } = hexToRgb(layer.hex);
  const image = ctx.createImageData(width, height);
  const pixels = image.data;
  for (let p = 0; p < layer.mask.length; p++) {
    if (!layer.mask[p]) continue;
    const i = p * 4;
    pixels[i] = r;
    pixels[i + 1] = g;
    pixels[i + 2] = b;
    pixels[i + 3] = 255;
  }
  ctx.putImageData(image, 0, 0);

  ctx.globalCompositeOperation = 'source-atop';
  ctx.save();
  ctx.translate(width / 2, height / 2);
  ctx.rotate((angleDeg * Math.PI) / 180);
  ctx.lineWidth = Math.max(0.5, HATCH_LINE_WIDTH * scale);

  const reach = Math.hypot(width, height);
  const spacing = Math.max(1, HATCH_SPACING * scale);
  let line = 0;
  for (let y = -reach; y < reach; y += spacing, line++) {
    ctx.strokeStyle = line % 2 ? HATCH_DARK : HATCH_LIGHT;
    ctx.beginPath();
    ctx.moveTo(-reach, y);
    ctx.lineTo(reach, y);
    ctx.stroke();
  }

  if (layer.metallic) {
    const sheen = ctx.createLinearGradient(-reach, 0, reach, 0);
    sheen.addColorStop(0, 'rgba(255,255,255,0)');
    sheen.addColorStop(0.5, 'rgba(255,255,255,0.38)');
    sheen.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = sheen;
    ctx.fillRect(-reach, -reach, 2 * reach, 2 * reach);
  }
  ctx.restore();

  return canvas;
}

export interface RenderResult {
  canvas: HTMLCanvasElement;
  /**
   * True when the canvas does not show the mapping: the source was tainted, no
   * 2D context was available, or no thread layer was painted. Fine to display —
   * it beats an empty box — but it must never be uploaded as the artwork,
   * because the seller would be shipping a motif nobody mapped.
   */
  degraded: boolean;
}

/**
 * Render the motif in stitch optics at the requested edge.
 */
export function renderEmbroideryPreview(options: RenderOptions): RenderResult {
  const { image, palette, mapping, threads, maxEdge } = options;
  const { width, height } = outputSize(image, maxEdge);

  const output = createCanvas(width, height);
  const outputCtx = output.getContext('2d');
  if (!outputCtx) return { canvas: output, degraded: true };

  const source = createCanvas(width, height);
  const sourceCtx = source.getContext('2d');
  if (!sourceCtx) return { canvas: output, degraded: true };
  sourceCtx.drawImage(image, 0, 0, width, height);

  let pixels: ImageData;
  try {
    pixels = sourceCtx.getImageData(0, 0, width, height);
  } catch {
    // Tainted canvas — the source was loaded without CORS. Show the untouched
    // motif rather than an empty box, but mark it: this is the unmapped image.
    outputCtx.drawImage(image, 0, 0, width, height);
    return { canvas: output, degraded: true };
  }

  const layers = mapPixelsToThreads(pixels, palette, mapping, threads);

  // One mask canvas is alive at a time: at print resolution a dozen full-size
  // canvases at once is where mobile Safari drops them.
  const scale = Math.max(width, height);
  layers.forEach((layer, index) => {
    const angle = HATCH_ANGLES[index % HATCH_ANGLES.length] ?? 0;
    const painted = paintThreadLayer(layer, width, height, angle, scale);

    outputCtx.save();
    outputCtx.shadowColor = SHADOW_COLOR;
    outputCtx.shadowBlur = SHADOW_BLUR * scale;
    outputCtx.shadowOffsetY = SHADOW_OFFSET_Y * scale;
    outputCtx.drawImage(painted, 0, 0);
    outputCtx.restore();
  });

  return { canvas: output, degraded: layers.length === 0 };
}

export function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Could not encode the embroidery render'));
    }, 'image/png');
  });
}
